"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { usePlatform } from "@/hooks/usePlatform";
import { APP_STORE_URL, PLAY_STORE_URL } from "@/lib/config";
import { SmsModal } from "./SmsModal";

type SendParcelContextValue = {
  /** Déclenche le comportement intelligent : store mobile ou modal SMS desktop */
  sendParcel: () => void;
  platform: ReturnType<typeof usePlatform>;
};

const SendParcelContext = createContext<SendParcelContextValue | null>(null);

export function useSendParcel() {
  const ctx = useContext(SendParcelContext);
  if (!ctx) {
    throw new Error("useSendParcel must be used within <SendParcelProvider>");
  }
  return ctx;
}

export function SendParcelProvider({ children }: { children: React.ReactNode }) {
  const platform = usePlatform();
  const [modalOpen, setModalOpen] = useState(false);

  const sendParcel = useCallback(() => {
    // L'app existe sur les deux magasins depuis le 25 août 2026.
    //   iPhone  → App Store
    //   Android → Google Play
    //   desktop → modale SMS, seul cas où l'on ignore quel téléphone
    //             recevra le lien : le SMS pointe donc vers /app, qui
    //             redirige lui-même vers le bon magasin.
    //
    // Avant cette date, Android tombait sur la modale en mode liste d'attente
    // faute d'app à installer. Ce détour n'a plus lieu d'être.
    if (platform === "ios") {
      window.location.href = APP_STORE_URL;
      return;
    }
    if (platform === "android") {
      window.location.href = PLAY_STORE_URL;
      return;
    }
    setModalOpen(true);
  }, [platform]);

  const value = useMemo(
    () => ({ sendParcel, platform }),
    [sendParcel, platform],
  );

  return (
    <SendParcelContext.Provider value={value}>
      {children}
      <SmsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        platform={platform}
      />
    </SendParcelContext.Provider>
  );
}
